/**
 * React-Hook für die Offline-Foto-Warteschlange.
 * Liefert die ausstehenden Fotos eines Projekts samt Online-/Flush-Status
 * und installiert beim ersten Mount die globalen Online-Listener.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ensureOnlineListener,
  flushQueue,
  isFlushing,
  isOfflineQueueSupported,
  isOnline,
  listPending,
  onFlushingChange,
  onQueueChange,
  type PendingPhotoRecord,
} from "@/lib/offline-photo-queue";

export interface OfflinePhotoQueueState {
  pending: PendingPhotoRecord[];
  count: number;
  online: boolean;
  flushing: boolean;
  supported: boolean;
  flushNow: () => Promise<{ uploaded: number; failed: number }>;
}

export function useOfflinePhotoQueue(projectId: string | undefined): OfflinePhotoQueueState {
  const [pending, setPending] = useState<PendingPhotoRecord[]>([]);
  const [online, setOnline] = useState<boolean>(isOnline());
  const [flushing, setFlushing] = useState<boolean>(isFlushing());
  const supported = useRef<boolean>(isOfflineQueueSupported()).current;

  useEffect(() => {
    ensureOnlineListener();
  }, []);

  useEffect(() => {
    if (!projectId) {
      setPending([]);
      return;
    }
    let cancelled = false;
    const refresh = () => {
      listPending(projectId)
        .then((rows) => {
          if (!cancelled) setPending(rows);
        })
        .catch(() => {
          if (!cancelled) setPending([]);
        });
    };
    refresh();
    const offChange = onQueueChange(refresh);
    const offFlush = onFlushingChange(setFlushing);
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    if (typeof window !== "undefined") {
      window.addEventListener("online", onOnline);
      window.addEventListener("offline", onOffline);
    }
    return () => {
      cancelled = true;
      offChange();
      offFlush();
      if (typeof window !== "undefined") {
        window.removeEventListener("online", onOnline);
        window.removeEventListener("offline", onOffline);
      }
    };
  }, [projectId]);

  const flushNow = useCallback(() => flushQueue(), []);

  return {
    pending,
    count: pending.length,
    online,
    flushing,
    supported,
    flushNow,
  };
}
